import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createImport } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ImportForm = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fabric_type: '',
    composition: '',
    weight_gsm: '',
    width_cm: '',
    color: '',
    finish: '',
    estimated_volume: '',
    expected_moq: '',
    desired_delivery_date: '',
    delivery_city: '',
    preferred_supplier_id: '',
    comments: '',
    reference_url: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const submitData = {
      ...formData,
      weight_gsm: formData.weight_gsm ? parseInt(formData.weight_gsm) : null,
      width_cm: formData.width_cm ? parseInt(formData.width_cm) : null,
      estimated_volume: formData.estimated_volume ? parseInt(formData.estimated_volume) : null,
      expected_moq: formData.expected_moq ? parseInt(formData.expected_moq) : null
    };

    try {
      await createImport(submitData);
      toast.success(isUSA ? 'Schedule created successfully' : 'Programación creada correctamente');
      navigate('/dashboard/imports');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="import-form">
      <div className="page-header">
        <h1 className="page-title">{isUSA ? 'New Import Schedule' : 'Nueva Programación de Importación'}</h1>
        <p className="page-subtitle">
          {isUSA ? 'Request imported fabric from specialized suppliers' : 'Solicita tela de importación a proveedores especializados'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Fabric specifications' : 'Especificaciones de la tela'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fabric_type">{isUSA ? 'Fabric type *' : 'Tipo de tela *'}</Label>
              <Input
                id="fabric_type"
                value={formData.fabric_type}
                onChange={(e) => setFormData({ ...formData, fabric_type: e.target.value })}
                placeholder={isUSA ? 'E.g.: Poplin, Denim, Jersey, etc.' : 'Ej: Popelina, Denim, Jersey, etc.'}
                required
                data-testid="fabric-type-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="composition">{isUSA ? 'Desired composition' : 'Composición deseada'}</Label>
                <Input
                  id="composition"
                  value={formData.composition}
                  onChange={(e) => setFormData({ ...formData, composition: e.target.value })}
                  placeholder={isUSA ? 'E.g.: 100% cotton, 65/35 poly/cotton' : 'Ej: 100% algodón, 65/35 poli/algodón'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight_gsm">{isUSA ? 'Approximate weight (g/m²)' : 'Gramaje aproximado (g/m²)'}</Label>
                <Input
                  id="weight_gsm"
                  type="number"
                  value={formData.weight_gsm}
                  onChange={(e) => setFormData({ ...formData, weight_gsm: e.target.value })}
                  placeholder="180"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="width_cm">{isUSA ? 'Width (cm)' : 'Ancho (cm)'}</Label>
                <Input
                  id="width_cm"
                  type="number"
                  value={formData.width_cm}
                  onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })}
                  placeholder="150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">{isUSA ? 'Required color' : 'Color requerido'}</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder={isUSA ? 'E.g.: White, Black, Navy blue' : 'Ej: Blanco, Negro, Azul marino'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finish">{isUSA ? 'Print or finish' : 'Estampado o acabado'}</Label>
              <Input
                id="finish"
                value={formData.finish}
                onChange={(e) => setFormData({ ...formData, finish: e.target.value })}
                placeholder={isUSA ? 'E.g.: Sanforized, Pre-shrunk, Digital print' : 'Ej: Sanforizado, Pre-encogido, Estampado digital'}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Volume and delivery' : 'Volumen y entrega'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated_volume">{isUSA ? 'Estimated volume (meters/kg)' : 'Volumen estimado (metros/kg)'}</Label>
                <Input
                  id="estimated_volume"
                  type="number"
                  value={formData.estimated_volume}
                  onChange={(e) => setFormData({ ...formData, estimated_volume: e.target.value })}
                  placeholder="5000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expected_moq">{isUSA ? 'Expected MOQ' : 'MOQ esperado'}</Label>
                <Input
                  id="expected_moq"
                  type="number"
                  value={formData.expected_moq}
                  onChange={(e) => setFormData({ ...formData, expected_moq: e.target.value })}
                  placeholder="1000"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="desired_delivery_date">{isUSA ? 'Desired delivery date' : 'Fecha deseada de entrega'}</Label>
                <Input
                  id="desired_delivery_date"
                  type="date"
                  value={formData.desired_delivery_date}
                  onChange={(e) => setFormData({ ...formData, desired_delivery_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_city">{isUSA ? 'Delivery city or port *' : 'Ciudad o puerto de entrega *'}</Label>
                <Input
                  id="delivery_city"
                  value={formData.delivery_city}
                  onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                  placeholder={isUSA ? 'E.g.: Los Angeles, Long Beach' : 'Ej: Ciudad de México, Manzanillo'}
                  required
                  data-testid="delivery-city-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Additional information' : 'Información adicional'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="comments">{isUSA ? 'Comments or special requirements' : 'Comentarios o requerimientos especiales'}</Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder={isUSA ? 'Describe any additional requirements...' : 'Describe cualquier requerimiento adicional...'}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference_url">{isUSA ? 'Visual reference (URL)' : 'Referencia visual (URL)'}</Label>
              <Input
                id="reference_url"
                value={formData.reference_url}
                onChange={(e) => setFormData({ ...formData, reference_url: e.target.value })}
                placeholder={isUSA ? 'https://example.com/reference.jpg' : 'https://ejemplo.com/referencia.jpg'}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {isUSA ? 'Cancel' : 'Cancelar'}
          </Button>
          <Button 
            type="submit" 
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={saving}
            data-testid="submit-import-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUSA ? 'Sending...' : 'Enviando...'}
              </>
            ) : (
              isUSA ? 'Create schedule' : 'Crear programación'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ImportForm;
